import React, { useEffect } from 'react';
import Tab from '@mui/material/Tab';
import ModalDialog from 'src/components/ModalDialog';
import DataTable, { getActionsColConfig } from 'src/components/DataTable';
import TimeFormattedValue from 'src/components/TimeFormattedValue';
import Tabs from 'src/components/Tabs';
import { useStoreUnitActions } from 'src/redux/helpers';
import { loadDataFromStorage } from 'src/utils';
import {
  DeploymentStatus,
  SolutionEdgeWorkloadItem,
  SolutionWorkloadStatusConditionsField,
  SolutionWorkloadStatusResourcesField,
} from 'src/pages/Solutions/types';
import { solutionsStore } from 'src/pages/Solutions';
import {
  useSolutionWorkloadConditions,
  useSolutionWorkloadResources,
} from 'src/pages/Solutions/redux';
import SolutionWorkloadStatus from './SiteWorkloadStatus';
import SolutionWorkloadStatusAction from './SiteWorkloadStatusActions';
import SolutionWorkloadMessage from './SiteWorkloadMessage';

type Props = {
  item: SolutionEdgeWorkloadItem;
  open: boolean;
  onClose: () => void;
};

const tableStyle = {
  minHeight: 0,
};

const resourcesColumns = [
  {
    id: SolutionWorkloadStatusResourcesField.Name,
    title: 'Name',
  },
  {
    id: SolutionWorkloadStatusResourcesField.Namespace,
    title: 'Namespace',
  },
  {
    id: SolutionWorkloadStatusResourcesField.Kind,
    title: 'Kind',
  },
  {
    id: SolutionWorkloadStatusResourcesField.ApiVersion,
    title: 'ApiVersion',
  },
  {
    id: SolutionWorkloadStatusResourcesField.Id,
    title: 'Cluster',
  },
  {
    id: SolutionWorkloadStatusResourcesField.State,
    title: 'State',
    Component: SolutionWorkloadStatus,
  },
  getActionsColConfig(SolutionWorkloadStatusAction),
];

const conditionsColumns = [
  {
    id: SolutionWorkloadStatusConditionsField.Type,
    title: 'Conditions',
    sx: { width: '1px', padding: 1 },
  },
  {
    id: SolutionWorkloadStatusConditionsField.Status,
    title: 'Status',
    Component: SolutionWorkloadStatus,
    sx: { paddingLeft: 0 },
  },
  {
    id: SolutionWorkloadStatusConditionsField.LastUpdateTime,
    title: 'Updated',
    Component: TimeFormattedValue,
    sx: { width: '1px', padding: 0 },
  },
  {
    id: SolutionWorkloadStatusConditionsField.Message,
    title: 'Message',
    Component: SolutionWorkloadMessage,
  },
];

function SolutionWorkloadStatusViewModal({ item, open, onClose }: Props) {
  const { name } = item;
  const actions = useStoreUnitActions(solutionsStore);
  const [data, loading] = useSolutionWorkloadResources();
  const [conditionsData] = useSolutionWorkloadConditions();
  const [type, setType] = React.useState(
    loadDataFromStorage('DeploymentStatus') || DeploymentStatus.Resources
  );
  const handleChange = (event: React.SyntheticEvent, newValue) => {
    setType(newValue);
    localStorage.removeItem('DeploymentStatus');
  };

  const displayConditionsData = conditionsData?.map((item) => ({
    ...item,
    message: item.message ? item.message : '',
  }));

  useEffect(() => {
    actions.solutionWorkloadStatus(name);
  }, [name]);

  return (
    <ModalDialog
      title={'Solution Workload Status'}
      open={open}
      onClose={onClose}
      onConfirm={onClose}
      loading={loading}
      showCancelBtn={false}
      confirmBtnText={'Close'}
      maxWidth="lg"
      fullWidth
    >
      <Tabs
        value={type}
        onChange={handleChange}
        aria-label="wrapped label tabs example"
        sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 3 }}
      >
        <Tab value={DeploymentStatus.Resources} label="Resources" />
        <Tab value={DeploymentStatus.Conditions} label="Conditions" />
      </Tabs>
      {type === DeploymentStatus.Resources && (
        <DataTable columns={resourcesColumns} data={data} sx={tableStyle} />
      )}
      {type === DeploymentStatus.Conditions && (
        <DataTable
          columns={conditionsColumns}
          data={displayConditionsData}
          sx={tableStyle}
        />
      )}
    </ModalDialog>
  );
}

export default SolutionWorkloadStatusViewModal;
